import { useState } from 'react';
import { formatMoney, type Account } from '@domain';
import type { Translate } from '../i18n/index.js';
import type { TrainingSetup, UnaccountedBills } from '../api.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';

/**
 * Setting the training module up, and checking nothing has fallen out.
 *
 * Two halves of one job. The top half asks which accounts carry a cost
 * that should belong to a course — an accounting question, answered by
 * whoever knows the chart of accounts. The bottom half lists the bills on
 * those accounts that are not on a course.
 *
 * They are on one screen on purpose. The flag is only as good as the
 * account list, and a company that has nominated nothing gets an empty
 * flag that would otherwise look like good news.
 */

export function TrainingSetupScreen({
  setup,
  unaccounted,
  accounts,
  loading,
  busy,
  canEdit,
  error,
  t,
  onSaveAccounts,
  onOpenBill,
}: {
  setup: TrainingSetup | null;
  unaccounted: UnaccountedBills | null;
  /** Every account from Xero, so the list is theirs rather than invented. */
  accounts: Account[];
  loading: boolean;
  busy: boolean;
  /** Choosing which accounts count is an 'operate' decision. */
  canEdit: boolean;
  error: string | null;
  t: Translate;
  onSaveAccounts: (codes: string[]) => void;
  onOpenBill: (xeroId: string) => void;
}) {
  const saved = setup?.directCostAccounts ?? [];
  const [chosen, setChosen] = useState<string[] | null>(null);
  const picked = chosen ?? saved;

  const dirty =
    chosen !== null &&
    (chosen.length !== saved.length || chosen.some((code) => !saved.includes(code)));

  /*
    Expense accounts only. A training cost is never revenue or a bank
    account, and showing ninety accounts to find four is how somebody
    ticks the wrong one.
  */
  const expenses = accounts.filter((account) =>
    ['EXPENSE', 'DIRECTCOSTS', 'OVERHEADS'].includes(account.type.trim().toUpperCase()),
  );

  const toggle = (code: string) => {
    const next = picked.includes(code)
      ? picked.filter((c) => c !== code)
      : [...picked, code];
    setChosen(next);
  };

  const cell = {
    padding: '9px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  if (loading) {
    return (
      <div style={{ padding: '22px 24px 40px', maxWidth: '1000px' }}>
        <Card>
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Loading…')}
          </div>
        </Card>
      </div>
    );
  }

  const tracking = setup?.tracking ?? null;

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1000px' }}>
      <PageTitle
        title={t('Training setup')}
        sub={t('Which costs belong to a course, and how the course reaches Xero.')}
      />
      <div style={{ height: '18px' }} />

      {/* ------------------------------------------------------------------
          How the course gets into Xero at all.
      ------------------------------------------------------------------ */}
      <Card style={{ marginBottom: '14px' }}>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('How Xero knows which course')}
        </div>
        <div style={{ padding: '14px 16px', fontSize: '12.5px', lineHeight: 1.7 }}>
          {tracking === null ? (
            <span style={{ color: theme.warnDeep, fontWeight: 600 }}>
              {t('Xero could not be read just now, so this is not known.')}
            </span>
          ) : tracking.categoryName === null ? (
            <>
              <div>
                {t('Nothing has been set up yet. The first course invoiced will create it.')}
              </div>
              <div style={{ color: theme.textMuted, marginTop: '4px' }}>
                {t('Tracking category slots free in Xero')}: {tracking.slotsFree} / 2
              </div>
            </>
          ) : (
            <>
              <div>
                {t('Every invoice and bill is stamped with its course, so Xero can split its own profit and loss by it.')}
              </div>
              <div style={{ color: theme.textMuted, marginTop: '4px' }}>
                {t('Tracking category')}: <strong>{tracking.categoryName}</strong>
              </div>
              {tracking.budget && (
                <div
                  style={{
                    marginTop: '4px',
                    color: tracking.budget.nearlyFull || tracking.budget.full
                      ? theme.warnDeep
                      : theme.textMuted,
                    fontWeight: tracking.budget.nearlyFull || tracking.budget.full ? 600 : 400,
                  }}
                >
                  {t('Courses in it')}: {tracking.budget.active} / 100
                  {tracking.budget.full
                    ? ` — ${t('full. Archive a finished course before adding another.')}`
                    : tracking.budget.nearlyFull
                      ? ` — ${t('nearly full. Archive the courses that are finished and paid.')}`
                      : ''}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* ------------------------------------------------------------------
          Which accounts count.
      ------------------------------------------------------------------ */}
      <Card style={{ marginBottom: '14px' }}>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span>{t('Accounts that carry a course cost')}</span>
          {dirty && canEdit && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onSaveAccounts(picked)}
              style={{
                height: '32px',
                padding: '0 14px',
                background: theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {t('Save')}
            </button>
          )}
        </div>

        <div style={{ padding: '12px 16px', fontSize: '12px', color: theme.textMuted, lineHeight: 1.7 }}>
          {t('Tick the accounts a training cost is posted to — trainer fees, materials, venue, catering. Rent, software and the audit fee are not course costs and should stay unticked.')}
          <div style={{ marginTop: '4px' }}>
            {t('A bill on a ticked account that is not on a course is listed below. Tick nothing and nothing is listed.')}
          </div>
        </div>

        {expenses.length === 0 ? (
          <div style={{ padding: '0 16px 16px', fontSize: '13px', color: theme.textMuted }}>
            {t('No expense accounts were found in Xero.')}
          </div>
        ) : (
          <div
            style={{
              padding: '0 16px 14px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '6px 18px',
            }}
          >
            {expenses.map((account) => (
              <label
                key={account.code}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '8px',
                  fontSize: '12.5px',
                  cursor: canEdit ? 'pointer' : 'default',
                  opacity: canEdit ? 1 : 0.7,
                }}
              >
                <input
                  type="checkbox"
                  disabled={!canEdit || busy}
                  checked={picked.includes(account.code)}
                  onChange={() => toggle(account.code)}
                />
                <span>
                  <span dir="ltr" style={{ fontFamily: theme.fontMono, color: theme.textMuted }}>
                    {account.code}
                  </span>{' '}
                  {account.name}
                </span>
              </label>
            ))}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '11px 16px',
              borderTop: `1px solid ${theme.dividerSoft}`,
              fontSize: '12.5px',
              color: theme.badDeep,
              fontWeight: 600,
            }}
          >
            {t(error)}
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------------------
          What has fallen out.
      ------------------------------------------------------------------ */}
      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Course costs not on a course')}
        </div>

        {unaccounted === null || !unaccounted.configured ? (
          <div style={{ padding: '16px', fontSize: '13px', color: theme.textMuted }}>
            {t('Tick some accounts above and anything missed will be listed here.')}
          </div>
        ) : unaccounted.unavailable ? (
          <div style={{ padding: '16px', fontSize: '13px', color: theme.warnDeep, fontWeight: 600 }}>
            {t('Xero could not be read just now, so nothing can be checked.')}
          </div>
        ) : unaccounted.bills.length === 0 ? (
          <div style={{ padding: '16px', fontSize: '13px', color: theme.goodDeep, fontWeight: 600 }}>
            {t('Nothing is missing. Every bill on those accounts is on a course.')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={heading}>{t('What')}</th>
                <th style={heading}>{t('Supplier')}</th>
                <th style={heading}>{t('Date')}</th>
                <th style={{ ...heading, textAlign: 'end' }}>{t('Amount')}</th>
                <th style={{ ...heading, width: '90px' }} />
              </tr>
            </thead>
            <tbody>
              {unaccounted.bills.map((bill) => (
                <tr key={bill.xeroId}>
                  <td style={cell}>
                    {bill.description || t('Bill')}
                    <div style={{ color: theme.textMuted, fontSize: '11px' }} dir="ltr">
                      {bill.accountCodes.join(', ')}
                    </div>
                  </td>
                  <td style={{ ...cell, color: theme.textMuted }}>{bill.supplier || '—'}</td>
                  <td style={{ ...cell, fontFamily: theme.fontMono, fontSize: '12px' }} dir="ltr">
                    {bill.date ?? '—'}
                  </td>
                  <td
                    style={{ ...cell, textAlign: 'end', fontFamily: theme.fontMono, direction: 'ltr' }}
                  >
                    {formatMoney(bill.subTotal)} {bill.currencyCode}
                  </td>
                  <td style={{ ...cell, textAlign: 'end' }}>
                    <button
                      type="button"
                      onClick={() => onOpenBill(bill.xeroId)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: theme.radiusControl,
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {t('Open')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/*
          Said plainly. A flag that silently stops at a page boundary is
          the kind that gets trusted when it should not be.
        */}
        {unaccounted?.truncated && (
          <div
            style={{
              padding: '11px 16px',
              borderTop: `1px solid ${theme.dividerSoft}`,
              fontSize: '11.5px',
              color: theme.warnDeep,
              fontWeight: 600,
            }}
          >
            {t('Only the most recent bills were checked. Older ones are not in this list.')}
          </div>
        )}
      </Card>
    </div>
  );
}
